import React, { useState } from 'react';
import {
  message,
  Col,
  Row,
  Spin,
  Switch,
  Typography,
  Tooltip,
  Button
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { PlusOutlined } from '@ant-design/icons';
import PageHeader from '../../../components/PageHeader/PageHeader';
import ContentCustom from '../../../components/ContentCustom/ContentCustom';
import useAuthContext from '../../../contexts/AuthContext';
import { Datatable } from '../../../components';
import useColumns from './columns';
import NewUserProfileDrawer from './NewUserProfileDrawer';

const MyBrokerTeam = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isNewUserDrawerVisible, setIsNewUserDrawerVisible] = useState(false);

  const [forceRefresh, setForceRefresh] = useState(false);
  const { dispatchAPI, user, setUser } = useAuthContext();
  const [recordToUpdate, setRecordToUpdate] = useState();

  const resourceName = 'users';

  const updateRecord = (record) => {
    setRecordToUpdate(record);
    setIsNewUserDrawerVisible(true);
  };

  const deleteRecord = async (id) => {
    try {
      await dispatchAPI('DELETE', { url: `/${resourceName}/${id}` });
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message.error(e.response.data.message);
    }
  };

  const columns = useColumns(updateRecord, deleteRecord);

  const updateInsurer = async (body) => {
    setIsLoading(true);
    try {
      await dispatchAPI('PATCH', {
        url: `insurers/${user?.insurer._id}`,
        body
      });
      message.success(t('settings.team.success.updateUser'));
    } catch (e) {
      if (e.response) message.error(e.response.status);
    }
    setIsLoading(false);
  };

  const updateFieldUseDirectionProfile = async (value) => {
    setUser({
      ...user,
      insurer: {
        ...user?.insurer,
        use_direction_profile: value
      }
    });

    await updateInsurer({ use_direction_profile: value });
  };

  const isReductScreen = useMediaQuery({ query: '(max-width: 992px)' });

  return (
    <>
      <PageHeader title={user?.insurer?.name} />
      <NewUserProfileDrawer
        isDrawerVisible={isNewUserDrawerVisible}
        setIsDrawerVisible={setIsNewUserDrawerVisible}
        setForceRefresh={setForceRefresh}
        forceRefresh={forceRefresh}
        recordToUpdate={recordToUpdate}
        setRecordToUpdate={setRecordToUpdate}
        insurer={user?.insurer}
      />
      <ContentCustom>
        <Row align="left" style={{ marginBottom: 22 }}>
          <Col
            xl={14}
            xs={24}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Tooltip title={t('settings.team.useDirectionProfileLong')}>
              <Switch
                className="direction-profile-switch"
                checkedChildren={t('settings.team.useDirectionProfile')}
                unCheckedChildren={t('settings.team.useDirectionProfile')}
                onChange={(value) => updateFieldUseDirectionProfile(value)}
                checked={user?.insurer?.use_direction_profile}
                style={{ minWidth: 140 }}
              />
            </Tooltip>
            {user?.insurer?.use_direction_profile && (
              <Typography.Text
                style={{
                  marginLeft: 32,
                  display: 'inline-block'
                }}
              >
                {t('settings.team.directionProfileText')}
              </Typography.Text>
            )}
          </Col>
        </Row>
        {isLoading ? (
          <Spin />
        ) : (
          <>
            {!user?.insurer?.use_direction_profile && (
              <>
                <Row
                  gutter={24}
                  justify="space-between"
                  style={{ margin: '8px -12px 16px -12px' }}
                >
                  <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography.Title level={4} style={{ marginBottom: 0 }}>
                      {t('settings.team.collaborators')}
                    </Typography.Title>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      onClick={() =>
                        setIsNewUserDrawerVisible(!isNewUserDrawerVisible)
                      }
                      style={{
                        width: isReductScreen ? 35 : 'auto',
                        padding: isReductScreen ? '6px 4px' : '4px 15px'
                      }}
                    >
                      <span
                        style={{
                          display: isReductScreen ? 'none' : 'inline'
                        }}
                      >
                        {t('buttons.create')}
                      </span>
                      <PlusOutlined
                        style={{
                          marginLeft: isReductScreen ? 0 : 8,
                          fontSize: 16
                        }}
                      />
                    </Button>
                  </Col>
                </Row>
                <Datatable
                  style={{ marginTop: 32 }}
                  resourceName={resourceName}
                  populate="insurer,permissionManagement.products_access.product"
                  extraQuery={`insurer=${user?.insurer?._id}&role=users:USER&active=true`}
                  columns={columns}
                  customActionColumn
                  forceRefresh={forceRefresh}
                  onDoubleClickAction={false}
                  scroll={{ x: 1100 }}
                />
              </>
            )}
          </>
        )}
      </ContentCustom>
    </>
  );
};

export default MyBrokerTeam;
